import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { ICamara, IPopulate, IQueryParam } from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';

@Component({
  selector: 'app-detalles-camara',
  templateUrl: './detalles-camara.component.html',
  styleUrl: '../../../../componentes/detalle/detalle-pagina.scss',
  standalone: false,
})
export class DetallesCamaraComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  private paramsService = inject(ParamsService);
  private helper = inject(HelperService);

  // Los mismos permisos que pide el listado para cada acción
  public puedeEditar = this.helper.tienePermiso('Cámaras - Editar cámara');
  public puedeVerStream = this.helper.tienePermiso('Cámaras - Ver streaming');

  public idCamara: string | null = null;
  public camara?: ICamara;
  public loading: boolean = true;
  public selectedTab = 0;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  /** Lo que identifica a la cámara: el host, o el número de serie en P2P. */
  get subtitulo(): string | undefined {
    return this.camara?.host || this.camara?.numeroSerie || undefined;
  }

  /**
   * Las pestañas en orden, por nombre: Video solo con permiso para ver el
   * streaming. Tiene que coincidir con la plantilla.
   */
  private pestanas(): string[] {
    return ['info', ...(this.puedeVerStream ? ['video'] : [])];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  private async listarCamara(): Promise<void> {
    // Con el listado filtrado por id y no con el getById: el getById no trae
    // el cliente ni el modelo (el chip de cliente y "Marca y modelo" salían
    // vacíos). Sin acceso o si no existe, viene vacío y la página lo dice.
    const populate: IPopulate[] = [
      { path: 'modeloDispositivo', select: 'marca modelo' },
      { path: 'cliente', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ _id: this.idCamara }),
      populate: JSON.stringify(populate),
      includeChildren: true,
    };
    try {
      const data = await firstValueFrom(this.lista2.camaras(query));
      this.camara = data.datos?.[0];
    } catch {
      this.camara = undefined;
    }
  }

  public editar(): void {
    this.paramsService.setParams({ edit: this.camara });
    this.router.navigate(['main/camaras/editar', this.idCamara]);
  }

  /**
   * Carga la cámara del id de la ruta. Se llama cada vez que cambia el id:
   * Angular reutiliza el componente al ir de una cámara a otra.
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.camara = undefined;
    this.idCamara = id;
    await this.listarCamara();
    this.loading = false;
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
